import { Head, Link, usePage, Form as InertiaForm } from '@inertiajs/react'
import TaxCategory from './tax_category'
import { TaxCategoryType, TaxDefinitionType } from './types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useEffect, useState } from 'react'

interface ShowProps {
  tax_category: TaxCategoryType
  tax_definitions: TaxDefinitionType[]
}

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const

export default function Show({ tax_category, tax_definitions }: ShowProps) {
  const { flash } = usePage()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [status, setStatus] = useState('active')

  useEffect(() => {
    if (flash?.notice) {
      setDialogOpen(false)
    }
  }, [flash?.notice])

  return (
    <>
      <Head title={`Tax category #${tax_category.id}`} />

      <div className="mx-auto md:w-2/3 w-full px-8 pt-8">
        <div className="mx-auto">
          {flash.notice && (
            <p className="py-2 px-3 bg-green-50 mb-5 text-green-500 font-medium rounded-lg inline-block">
              {flash.notice}
            </p>
          )}

          <h1 className="font-bold text-4xl">Tax category #{tax_category.id}</h1>

          <TaxCategory tax_category={tax_category} />

          <div className="mt-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold">Tax definitions</h2>
              <Button onClick={() => setDialogOpen(true)}>Add definition</Button>
            </div>
            <div className="mt-4 space-y-4">
              {tax_definitions.map((definition) => (
                <div
                  key={definition.id}
                  className="rounded-lg border p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{definition.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {definition.slug}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-muted-foreground">
                        {definition.status}
                      </div>
                      <Link
                        href={`/tax_definitions/${definition.id}`}
                        className="rounded-md border px-3 py-1 text-xs font-medium hover:bg-accent"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                  {definition.description && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      {definition.description}
                    </p>
                  )}
                </div>
              ))}
              {tax_definitions.length === 0 && (
                <div className="text-sm text-muted-foreground">
                  No tax definitions yet.
                </div>
              )}
            </div>
          </div>

          <Link
            href={`/tax_categories/${tax_category.id}/edit`}
            className="mt-2 rounded-lg py-3 px-5 bg-gray-100 inline-block font-medium"
          >
            Edit this tax category
          </Link>
          <Link
            href="/tax_categories"
            className="ml-2 rounded-lg py-3 px-5 bg-gray-100 inline-block font-medium"
          >
            Back to tax categories
          </Link>
          <div className="inline-block ml-2">
            <Link
              href={`/tax_categories/${tax_category.id}`}
              as="button"
              method="delete"
              className="mt-2 rounded-lg py-3 px-5 bg-gray-100 font-medium"
            >
              Destroy this tax category
            </Link>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New tax definition</DialogTitle>
          </DialogHeader>
          <InertiaForm
            transform={(data) => ({ tax_definition: data })}
            method="post"
            action={`/tax_categories/${tax_category.id}/tax_definitions`}
            className="space-y-4"
            onSuccess={() => setDialogOpen(false)}
          >
            {({ errors, processing }) => (
              <>
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" placeholder="Definition name" />
                  {errors.name && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.name.join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="slug">Slug</Label>
                  <Input id="slug" name="slug" placeholder="definition-slug" />
                  {errors.slug && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.slug.join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <input type="hidden" name="status" value={status} />
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.status && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.status.join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder="Description"
                  />
                  {errors.description && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.description.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={processing}>
                    Create definition
                  </Button>
                </div>
              </>
            )}
          </InertiaForm>
        </DialogContent>
      </Dialog>
    </>
  )
}
